import { lazy, Suspense, useEffect } from 'react';
import { FeedContainer } from '@/features/camera-feeds/components/FeedContainer';
import { useParams } from 'react-router';
import { useSetAtom } from 'jotai';
import { CameraGridSkeleton, Controls } from '@/components/camera';
import { DollHouseSkeleton } from '@/features/dollhouse/components';
import { ActivitiesSkeleton, ActivityContainer } from '@/features/activity';
import { selectedClientIdAtom } from '@/state/clients';
import { VideoPlayer } from '@/components/video/player';

// Lazy load heavy components
const DollHouse = lazy(() =>
  import('@/features/dollhouse/components/DollHouse').then((module) => ({
    default: module.DollHouse,
  })),
);

export const PropertyPage = () => {
  // Get propertyId from URL params (this is the client ID)
  const { propertyId } = useParams<{ propertyId: string }>();

  // Set the selected client ID from the route parameter
  const setSelectedClientId = useSetAtom(selectedClientIdAtom);
  
  // Set the selected client ID when the route parameter changes
  useEffect(() => {
    if (propertyId) {
      setSelectedClientId(propertyId);
      console.log('Setting selected client ID from route:', propertyId);
    }
  }, [propertyId, setSelectedClientId]);
  
  if (!propertyId) {
    throw new Error('Property ID is required in the URL');
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 h-full gap-4 p-4 pt-[var(--header-height)]">
      {/* Left column - smaller (1/3 width) */}
      <div className="lg:col-span-1 flex flex-col gap-4">
        {/* Dollhouse widget */}
      
        <Suspense fallback={<DollHouseSkeleton />}>
          <DollHouse width="100%" height={370}/>
        </Suspense>
   
        {/* Controls widget */}
        <Controls />

        {/* Activity widget */}
        <div className="overflow-hidden">
          <Suspense fallback={<ActivitiesSkeleton />}>
            <ActivityContainer />
          </Suspense>
       </div>
      </div>

      {/* Right column - larger (2/3 width) */}
      <div className="lg:col-span-2 flex flex-col gap-4">
        {/* Video grid widget */}
        <div className="overflow-hidden">
          <Suspense fallback={<CameraGridSkeleton />}>
            <FeedContainer propertyId={propertyId} />
          </Suspense>
        </div>

        {/* Activity Video Player */}
        <VideoPlayer />
      </div>
    </div>
  );
};
